import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../api';
import { useMeta } from '../meta';
import { ErrorBox, IssueTable, Pager, useClickOutside, useIssueColumns } from '../components';
import { setLastList } from '../recent';
import type { IssueList, ProjectInfo, UserRef } from '../types';
import { t, tError, tp } from '../i18n';

export function ViewSwitch({ current }: { current: 'list' | 'board' }) {
  return (
    <div className="segmented">
      <Link to="/issues" className={current === 'list' ? 'on' : ''}>
        ☰ {t('List')}
      </Link>
      <Link to="/board" className={current === 'board' ? 'on' : ''}>
        ▦ {t('Board')}
      </Link>
    </div>
  );
}

export function MultiSelect({ label, options, value, onChange }: { label: string; options: { value: string; label: string }[]; value: string[]; onChange: (v: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false), open);
  const chosen = options.filter((o) => value.includes(o.value)).map((o) => o.label);
  return (
    <div className="filter-chip-wrap" ref={ref}>
      <button type="button" className={`filter-chip ${value.length ? 'active' : ''}`} onClick={() => setOpen(!open)} title={chosen.join(', ')}>
        <span className="fc-label">{label}</span>
        {value.length > 0 && <span className="fc-value">{chosen.length > 2 ? t('{n} selected', { n: chosen.length }) : chosen.join(', ')}</span>}
        <span className="fc-caret">▾</span>
      </button>
      {open && (
        <div className="menu left filter-menu">
          {/* always rendered so the options don't shift when the first one is checked */}
          <button type="button" className="menu-item subtle" disabled={value.length === 0} onClick={() => onChange([])}>
            {t('Clear selection')}
          </button>
          {options.map((o) => (
            <label key={o.value} className="menu-check">
              <input
                type="checkbox"
                checked={value.includes(o.value)}
                onChange={(e) => onChange(e.target.checked ? [...value, o.value] : value.filter((v) => v !== o.value))}
              />
              {o.label}
            </label>
          ))}
          {options.length === 0 && <div className="menu-empty">{t('No options')}</div>}
        </div>
      )}
    </div>
  );
}

const FILTER_KEYS = ['status', 'handler', 'reporter', 'monitor', 'category', 'severity', 'priority', 'resolution', 'target_version', 'fixed_in_version', 'version', 'tag', 'search', 'view_state', 'updated_days'];

export function IssueListPage() {
  const { meta, projectId, items, project } = useMeta();
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [searchText, setSearchText] = useState(params.get('search') ?? '');
  const listRef = useRef<HTMLElement>(null);

  // the project in the URL wins; otherwise the globally selected one
  const effective = useMemo(() => {
    const p: Record<string, string> = Object.fromEntries(params.entries());
    if (!('project' in p) && projectId) p.project = String(projectId);
    return p;
  }, [params, projectId]);
  const pid = Number(effective.project) || 0;

  const { data, error, isFetching } = useQuery({
    queryKey: ['issues', effective],
    queryFn: () => api.get<IssueList>('/api/issues' + qs(effective)),
    placeholderData: (prev) => prev,
  });
  const projectInfo = useQuery({ queryKey: ['project', pid], queryFn: () => api.get<ProjectInfo>(`/api/projects/${pid}`), enabled: pid > 0 });
  const options = useQuery({
    queryKey: ['filter-options'],
    queryFn: () => api.get<{ categories: string[]; versions: string[]; custom_fields: any[] }>('/api/filter-options'),
    enabled: pid === 0,
  });
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<(UserRef & { enabled: boolean })[]>('/api/users') });
  const filters = useQuery({ queryKey: ['filters'], queryFn: () => api.get<any[]>('/api/filters') });

  useEffect(() => setSelected(new Set()), [effective]);
  useEffect(() => setSearchText(params.get('search') ?? ''), [params]);
  useEffect(() => {
    if (data) setLastList(data.issues.map((i) => i.id));
  }, [data]);

  const update = (changes: Record<string, string | string[] | undefined>, resetPage = true) => {
    const next = new URLSearchParams(params);
    if (!next.has('project') && projectId) next.set('project', String(projectId));
    for (const [k, v] of Object.entries(changes)) {
      const s = Array.isArray(v) ? v.join(',') : v;
      if (s === undefined || s === '') next.delete(k);
      else next.set(k, s);
    }
    if (resetPage) next.delete('page');
    setParams(next);
  };
  const get = (k: string) => (effective[k] ? effective[k].split(',') : []);

  const categories = pid ? [...new Set((projectInfo.data?.categories ?? []).map((c) => c.name))] : (options.data?.categories ?? []);
  const versions = pid ? [...new Set((projectInfo.data?.versions ?? []).map((v) => v.version))] : (options.data?.versions ?? []);
  const userOpts = (users.data ?? []).filter((u) => u.enabled).map((u) => ({ value: String(u.id), label: u.realname || u.username }));
  const columns = useIssueColumns({ showProject: !pid || meta.projects.some((p) => p.parent_ids.includes(pid)) });

  const hide = effective.hide_status ?? '90';
  const sort = effective.sort ?? 'updated';
  const dir = effective.dir ?? 'desc';
  const onSort = (key: string) =>
    update({ sort: key, dir: sort === key && dir === 'desc' ? 'asc' : sort === key ? 'desc' : key === 'updated' ? 'desc' : 'asc' }, false);

  const saveFilter = useMutation({ mutationFn: (body: any) => api.post('/api/filters', body), onSuccess: () => qc.invalidateQueries({ queryKey: ['filters'] }) });
  const deleteFilter = useMutation({ mutationFn: (id: number) => api.del(`/api/filters/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ['filters'] }) });

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    update({ search: searchText.trim() || undefined });
  };
  const activeFilterCount = FILTER_KEYS.filter((k) => effective[k]).length;

  return (
    <div className="page">
      <section className="card filters issue-list-controls">
        <div className="page-head issue-list-head">
          <h1>{t('View Issues')}</h1>
          <span className="subtle">{pid ? project(pid)?.name : t('All Projects')}</span>
          <label className="inline-label">
            {t('Hide status')}
            <select value={hide} onChange={(e) => update({ hide_status: e.target.value })} disabled={get('status').length > 0}>
              <option value="0">{t('[none]')}</option>
              <option value="80">{t('resolved and above')}</option>
              <option value="90">{t('closed')}</option>
            </select>
          </label>
          <span className="spacer" />
          <select
            value=""
            onChange={(e) => {
              const f = filters.data?.find((x) => String(x.id) === e.target.value);
              if (f) setParams(new URLSearchParams(f.criteria));
            }}
            aria-label={t('Saved filters')}
          >
            <option value="">{t('Saved filters…')}</option>
            {filters.data?.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
                {f.is_public ? ` (${t('shared by {user}', { user: f.username })})` : ''}
              </option>
            ))}
          </select>
          <button
            className="btn"
            onClick={() => {
              const name = prompt(t('Name for this filter:'));
              if (!name) return;
              const isPublic = confirm(t('Share this filter with everyone? (Cancel = only me)'));
              const criteria = { ...effective };
              delete criteria.page;
              saveFilter.mutate({ name, is_public: isPublic, project_id: pid || null, criteria });
            }}
          >
            {t('Save Current Filter')}
          </button>
          {filters.data && filters.data.some((f) => f.user_id === meta.user.id || meta.user.access_level >= 90) && (
            <select
              value=""
              onChange={(e) => {
                const id = Number(e.target.value);
                if (id && confirm(t('Delete this saved filter?'))) deleteFilter.mutate(id);
              }}
              aria-label={t('Delete saved filter')}
            >
              <option value="">{t('Delete filter…')}</option>
              {filters.data
                .filter((f) => f.user_id === meta.user.id || meta.user.access_level >= 90)
                .map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
            </select>
          )}
          <div className="head-actions">
            <a className="btn" href={'/api/issues' + qs({ ...effective, format: 'csv', page: undefined })}>
              ⭳ {t('CSV Export')}
            </a>
            <ViewSwitch current="list" />
          </div>
        </div>
        <div className="filter-top">
          {/* always rendered so the filter chips (and an open menu) don't shift when the first filter is set */}
          <button type="button" className="btn" disabled={activeFilterCount === 0} onClick={() => setParams(pid ? { project: String(pid) } : {})}>
            × {t('Clear filters')}
          </button>
          <form onSubmit={submitSearch} className="search-field">
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
              <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="2" fill="none" />
              <path d="M20 20l-4.5-4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder={t('Search summary, description, notes or #id')} />
          </form>
          <MultiSelect label={t('Status')} options={items('status').map((s) => ({ value: String(s.value), label: s.label }))} value={get('status')} onChange={(v) => update({ status: v })} />
          <MultiSelect label={t('Assigned To')} options={[{ value: 'me', label: t('[Myself]') }, { value: 'none', label: t('[None]') }, ...userOpts]} value={get('handler')} onChange={(v) => update({ handler: v })} />
          <MultiSelect label={t('Reporter')} options={[{ value: 'me', label: t('[Myself]') }, ...userOpts]} value={get('reporter')} onChange={(v) => update({ reporter: v })} />
          <MultiSelect label={t('Category')} options={categories.map((c) => ({ value: c, label: c }))} value={get('category')} onChange={(v) => update({ category: v })} />
          <MultiSelect label={t('Severity')} options={items('severity').map((s) => ({ value: String(s.value), label: s.label }))} value={get('severity')} onChange={(v) => update({ severity: v })} />
          <MultiSelect label={t('Priority')} options={items('priority').map((s) => ({ value: String(s.value), label: s.label }))} value={get('priority')} onChange={(v) => update({ priority: v })} />
          <MultiSelect label={t('Resolution')} options={items('resolution').map((s) => ({ value: String(s.value), label: s.label }))} value={get('resolution')} onChange={(v) => update({ resolution: v })} />
          <MultiSelect label={t('Target Version')} options={versions.map((v) => ({ value: v, label: v }))} value={get('target_version')} onChange={(v) => update({ target_version: v })} />
          <MultiSelect label={t('Fixed in Version')} options={versions.map((v) => ({ value: v, label: v }))} value={get('fixed_in_version')} onChange={(v) => update({ fixed_in_version: v })} />
          <MultiSelect label={t('Tags')} options={meta.tags.map((tg) => ({ value: String(tg.id), label: tg.name }))} value={get('tag')} onChange={(v) => update({ tag: v })} />
          <button className={`filter-chip ${effective.monitor === 'me' ? 'active' : ''}`} onClick={() => update({ monitor: effective.monitor === 'me' ? undefined : 'me' })}>
            {t('Monitored by me')}
          </button>
        </div>
      </section>

      <ErrorBox error={error} />
      {data && (
        <section className="card" ref={listRef} style={{ scrollMarginTop: 72 /* below the sticky top bar */ }}>
          <div className="card-head">
            <h2>
              {t('Viewing Issues ({from} - {to} / {total})', {
                from: data.total === 0 ? 0 : (data.page - 1) * data.per_page + 1,
                to: Math.min(data.total, data.page * data.per_page),
                total: data.total,
              })}
              {isFetching && <span className="subtle small"> {t('updating…')}</span>}
            </h2>
            <label className="inline-label small per-page">
              {t('Per page')}
              <select value={effective.per_page ?? '50'} onChange={(e) => update({ per_page: e.target.value })}>
                {[5, 10, 25, 50, 100, 250, 500].map((n) => (
                  <option key={n}>{n}</option>
                ))}
              </select>
            </label>
            <Pager page={data.page} perPage={data.per_page} total={data.total} onPage={(p) => update({ page: String(p) }, false)} compact />
          </div>
          {selected.size > 0 && <BulkBar ids={[...selected]} pid={pid} onDone={() => setSelected(new Set())} />}
          <IssueTable issues={data.issues} columns={columns} sort={sort} dir={dir} onSort={onSort} selected={selected} onSelect={setSelected} stickyFirst={!effective.sort} />
          {/* from the bottom, go back up to the start of the list on the new page */}
          <Pager
            page={data.page}
            perPage={data.per_page}
            total={data.total}
            onPage={(p) => {
              update({ page: String(p) }, false);
              listRef.current?.scrollIntoView({ block: 'start' });
            }}
          />
        </section>
      )}
    </div>
  );
}

function BulkBar({ ids, pid, onDone }: { ids: number[]; pid: number; onDone: () => void }) {
  const { items, meta, level, th } = useMeta();
  const qc = useQueryClient();
  const [action, setAction] = useState('assign');
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [result, setResult] = useState('');
  const projectInfo = useQuery({ queryKey: ['project', pid], queryFn: () => api.get<ProjectInfo>(`/api/projects/${pid}`), enabled: pid > 0 });
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<(UserRef & { enabled: boolean })[]>('/api/users') });

  const run = useMutation({
    mutationFn: () => api.post<{ results: { id: number; ok: boolean; error?: string }[] }>('/api/issues/bulk', { ids, action, value: value === '' ? null : value, note }),
    onSuccess: (res) => {
      const failed = res.results.filter((r) => !r.ok);
      setResult(
        failed.length
          ? t('{ok} updated, {failed} failed: ', { ok: res.results.length - failed.length, failed: failed.length }) +
              failed.map((f) => `#${f.id}: ${tError(f.error ?? '')}`).join('; ')
          : tp(res.results.length, '{n} issue updated.', '{n} issues updated.'),
      );
      qc.invalidateQueries({ queryKey: ['issues'] });
      qc.invalidateQueries({ queryKey: ['myview'] });
      if (!failed.length) onDone();
    },
  });

  const opts = (list: { value: string | number; label: string }[], empty?: string) => (
    <select value={value} onChange={(e) => setValue(e.target.value)}>
      {empty !== undefined && <option value="">{empty}</option>}
      {list.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
  const valueInput = () => {
    switch (action) {
      case 'assign':
        return opts(((pid ? projectInfo.data?.handlers : users.data?.filter((u) => u.enabled)) ?? []).map((u) => ({ value: u.id, label: u.realname || u.username })), t('[Nobody]'));
      case 'status':
        return opts(items('status').map((s) => ({ value: s.value, label: s.label })), t('choose…'));
      case 'resolve':
        return opts(items('resolution').filter((r) => r.value !== 10 && r.value !== 30).map((s) => ({ value: s.value, label: s.label })));
      case 'priority':
        return opts(items('priority').map((s) => ({ value: s.value, label: s.label })), t('choose…'));
      case 'move':
        return opts(meta.projects.filter((p) => p.access >= th('move_bug')).map((p) => ({ value: p.id, label: p.name })), t('choose project…'));
      case 'category':
        return pid ? opts((projectInfo.data?.categories ?? []).map((c) => ({ value: c.id, label: c.name })), t('choose…')) : <span className="subtle small">{t('select a project first')}</span>;
      case 'target_version':
      case 'fixed_in_version':
        return pid ? opts((projectInfo.data?.versions ?? []).map((v) => ({ value: v.version, label: v.version })), t('[none]')) : <input value={value} onChange={(e) => setValue(e.target.value)} placeholder={t('version')} />;
      case 'view_state':
        return opts([{ value: 10, label: t('public') }, { value: 50, label: t('private') }]);
      default:
        return null;
    }
  };

  return (
    <div className="bulkbar">
      <strong>{t('{n} selected', { n: ids.length })}</strong>
      <select
        value={action}
        onChange={(e) => {
          setAction(e.target.value);
          setValue(e.target.value === 'resolve' ? '20' : e.target.value === 'view_state' ? '10' : '');
          setResult('');
        }}
      >
        <option value="assign">{t('Assign')}</option>
        <option value="status">{t('Update Status')}</option>
        <option value="resolve">{t('Resolve')}</option>
        <option value="close">{t('Close')}</option>
        <option value="priority">{t('Update Priority')}</option>
        <option value="category">{t('Update Category')}</option>
        <option value="target_version">{t('Update Target Version')}</option>
        <option value="fixed_in_version">{t('Update Fixed in Version')}</option>
        <option value="view_state">{t('Update View Status')}</option>
        <option value="move">{t('Move')}</option>
        <option value="note">{t('Add Note')}</option>
        {level(pid || undefined) >= th('delete_bug') && <option value="delete">{t('Delete')}</option>}
      </select>
      {valueInput()}
      <input className="grow" value={note} onChange={(e) => setNote(e.target.value)} placeholder={action === 'note' ? t('Note text (required)') : t('Optional note')} />
      <button
        className="btn primary"
        disabled={run.isPending}
        onClick={() => {
          if (action === 'delete' && !confirm(tp(ids.length, 'Delete {n} issue permanently?', 'Delete {n} issues permanently?'))) return;
          run.mutate();
        }}
      >
        {t('OK')}
      </button>
      {run.error && <span className="error">{tError((run.error as Error).message)}</span>}
      {result && <span className="small">{result}</span>}
    </div>
  );
}
